import { useState } from "react";
import { Link } from "react-router-dom";
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useFetch } from "@/hooks/useFetch";
import { PEOPLE, spaceOf } from "@/lib/spaces";
import { SalesHeader, WarmthBadge } from "@/components/sales/ui";
import { STAGES } from "@/components/sales/constants";

const Card = ({ c }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: c.id });
  const style = transform ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 50 } : {};
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes} data-testid={`pipeline-card-${c.id}`}
      className={`cursor-grab touch-none rounded-xl border bg-background/70 p-3 text-sm ${isDragging ? "shadow-2xl" : ""}`}>
      <Link to={`/sales/companies/${c.id}`} onPointerDown={(e) => e.stopPropagation()} className="font-semibold hover:text-cyan-300">{c.name}</Link>
      <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <WarmthBadge w={c.warmth} />{c.value ? <span className="font-mono">£{Number(c.value).toLocaleString()}</span> : null}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{c.owner ? spaceOf(c.owner).name : "Unassigned"}{c.next_action ? ` · ${c.next_action}` : ""}</p>
    </div>
  );
};

const Column = ({ s, items }) => {
  const { setNodeRef, isOver } = useDroppable({ id: s.key });
  return (
    <div ref={setNodeRef} data-testid={`pipeline-col-${s.key}`} className={`w-64 shrink-0 rounded-2xl border p-3 ${isOver ? "border-cyan-400/60 bg-cyan-400/5" : "bg-card/50"}`}>
      <p className="mb-3 flex items-center justify-between text-xs font-semibold uppercase tracking-wider" style={{ color: s.color }}>{s.label}<span className="font-mono text-muted-foreground">{items.length}</span></p>
      <div className="space-y-2">{items.map((c) => <Card key={c.id} c={c} />)}</div>
    </div>
  );
};

export default function Pipeline() {
  const { run } = useApp();
  const [owner, setOwner] = useState("");
  const [industry, setIndustry] = useState("");
  const items = useFetch("/sales/companies", { ...(owner ? { owner } : {}), ...(industry ? { industry } : {}) }) || [];
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = ({ active, over }) => {
    const c = items.find((x) => x.id === active.id);
    if (over && c && c.stage !== over.id) run(() => api.patch(`/sales/companies/${c.id}`, { stage: over.id }), `Moved to ${STAGES.find((s) => s.key === over.id).label}`);
  };
  return (
    <div data-testid="pipeline-page">
      <SalesHeader title="Pipeline" subtitle="Drag a company to move it to the next stage.">
        <select value={owner} onChange={(e) => setOwner(e.target.value)} className="h-10 rounded-full border bg-card px-3 text-sm" data-testid="pipeline-owner-filter"><option value="">Everyone</option>{PEOPLE.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}</select>
        <input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Industry" className="h-10 w-36 rounded-full border bg-card px-3 text-sm" data-testid="pipeline-industry-filter" />
      </SalesHeader>
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="scrollbar-thin flex gap-3 overflow-x-auto pb-4">{STAGES.map((s) => <Column key={s.key} s={s} items={items.filter((c) => c.stage === s.key)} />)}</div>
      </DndContext>
    </div>
  );
}
